import React, { useState } from "react";
import { X, Loader2, CheckCircle2, ShoppingBag } from "lucide-react";
import { api } from "../api";
import { useCart } from "../context/CartContext";

const COUNTRIES = ["United Kingdom", "Ireland", "United States", "Canada", "Australia", "Germany", "France", "Spain", "Netherlands", "Sweden", "New Zealand", "South Africa", "Italy", "Norway"];

const CheckoutModal = ({ open, onClose }) => {
  const { items, subtotal, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", country: "United Kingdom", postcode: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = {
        customer: form,
        items: items.map((i) => ({ product_id: i.id.replace(/-sub$/, ""), name: i.name, price: i.price, qty: i.qty, subscription: i.id.endsWith("-sub") })),
      };
      const r = await api.post("/store/checkout", payload);
      setDone(r.data);
      clear();
    } catch (err) {
      setError(err?.response?.data?.detail || "Checkout failed. Please check your details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#06160f]/80 backdrop-blur-sm" onClick={done ? () => { setDone(null); onClose(); } : onClose} />
      <div className="relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
        {done ? (
          <div className="py-6 text-center">
            <CheckCircle2 size={56} className="mx-auto text-[#2D6A4F]" />
            <h2 className="mt-4 font-display text-2xl font-bold uppercase text-[#1B4332]">Order Placed!</h2>
            <p className="mt-2 text-[#4a5c51]">Your order <strong>{done.order_id}</strong> is confirmed. A confirmation email is on its way.</p>
            <p className="mt-1 font-display text-xl font-bold text-[#1B4332]">Total: £{Number(done.total).toFixed(2)}</p>
            <button onClick={() => { setDone(null); onClose(); }} className="mt-6 rounded-full bg-[#2D6A4F] px-8 py-3 font-display text-sm font-bold uppercase tracking-wider text-white">Done</button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold uppercase text-[#1B4332]"><ShoppingBag size={20} /> Checkout</h2>
              <button onClick={onClose}><X size={22} className="text-[#1B4332]" /></button>
            </div>
            <div className="mb-4 rounded-xl bg-[#f0f7f2] p-3 text-center text-sm text-[#4a5c51]">{items.reduce((s, i) => s + i.qty, 0)} item(s) · Subtotal <strong>£{subtotal.toFixed(2)}</strong></div>
            <form onSubmit={submit} className="space-y-3">
              <In label="Full name" value={form.name} onChange={(v) => set("name", v)} />
              <In label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} />
              <In label="Address" value={form.address} onChange={(v) => set("address", v)} />
              <div className="grid grid-cols-2 gap-3">
                <In label="City" value={form.city} onChange={(v) => set("city", v)} />
                <In label="Postcode" value={form.postcode} onChange={(v) => set("postcode", v)} />
              </div>
              <div>
                <label className="mb-1 block font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F]">Country</label>
                <select value={form.country} onChange={(e) => set("country", e.target.value)} className="w-full rounded-xl border border-[#dceadf] px-4 py-2.5 text-sm outline-none focus:border-[#52B788]">
                  {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              {error && <p className="rounded-lg bg-[#fbe9e9] px-4 py-2 text-sm text-[#c1121f]">{error}</p>}
              <button type="submit" disabled={busy || !items.length} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#2D6A4F] py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-[#1B4332] disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />} Place Order · £{subtotal.toFixed(2)}
              </button>
              <p className="text-center text-xs text-[#8aa093]">Demo checkout — no payment is taken.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const In = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="mb-1 block font-display text-xs font-semibold uppercase tracking-wider text-[#2D6A4F]">{label}</label>
    <input type={type} value={value} required onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-[#dceadf] px-4 py-2.5 text-sm outline-none focus:border-[#52B788]" />
  </div>
);

export default CheckoutModal;
